import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppConfig, DEFAULT_CONFIG, NavigationView, DEFAULT_CATEGORIES, WatchlistItem, DomainCategory } from "./types";
import Dashboard from "./components/Dashboard";
import BackorderManager from "./components/BackorderManager";
import BidsManager from "./components/BidsManager";
import HistoryView from "./components/HistoryView";
import Settings from "./components/Settings";
import { DropCatchService } from "./services/dropcatchService";
import { GeminiService } from "./services/geminiService";
import { ResendService } from "./services/resendService";
import { storageService } from "./services/storageService";
// Auth components
import LoginPage from "./components/auth/LoginPage";
import SignupPage from "./components/auth/SignupPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import VerifyEmailPage from "./components/auth/VerifyEmailPage";
import { sendVerificationEmail } from "./lib/auth-client";
import { useAuth } from "./hooks/useAuth";
// New components
import { AuctionSearch } from "./components/pages/AuctionSearch";
import { ChatPage } from "./components/pages/ChatPage";
import { Analytics } from "./components/pages/Analytics";
import { Watchlist } from "./components/pages/Watchlist";
import { Portfolio } from "./components/pages/Portfolio";
import { StatusPage } from "./components/pages/StatusPage";
import { Automations } from "./components/pages/Automations";
import { WhoisLookup } from "./components/pages/WhoisLookup";
import LandingPage from "./components/pages/LandingPage";
import LogoCircuit from "./components/logos/LogoCircuit";
// CategoryManager available for Settings integration: import { CategoryManager } from "./components/pages/CategoryManager";
// New services
import { getBatchAnalysisService } from "./services/batchAnalysisService";
import { createPortfolioService } from "./services/portfolioService";
import { alertService } from "./services/alertService";

// Initialize services
const dropcatchService = new DropCatchService(DEFAULT_CONFIG);
const geminiService = new GeminiService(DEFAULT_CONFIG.google_api_key);
const resendService = new ResendService(DEFAULT_CONFIG.resend_api_key);

type AuthView = "login" | "signup" | "forgot-password" | "reset-password" | "verify-email";

// Auth routes (public, no session required)
const authRoutes: Record<string, AuthView> = {
  '/login': 'login',
  '/signup': 'signup',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/verify-email': 'verify-email',
};

// Protected routes (require session)
const routeToView: Record<string, NavigationView> = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/search': 'auctions',
  '/auctions': 'auctions',
  '/backorders': 'backorders',
  '/bids': 'bids',
  '/watchlist': 'watchlist',
  '/portfolio': 'portfolio',
  '/analytics': 'analytics',
  '/history': 'history',
  '/chat': 'chat',
  '/automations': 'automations',
  '/whois': 'whois',
  '/status': 'status',
  '/settings': 'settings',
};

const viewToRoute: Record<NavigationView, string> = {
  'dashboard': '/dashboard',
  'auctions': '/search',
  'backorders': '/backorders',
  'bids': '/bids',
  'watchlist': '/watchlist',
  'portfolio': '/portfolio',
  'analytics': '/analytics',
  'history': '/history',
  'chat': '/chat',
  'automations': '/automations',
  'whois': '/whois',
  'status': '/status',
  'settings': '/settings',
};

// Get initial view from URL
function getInitialView(): NavigationView {
  const path = window.location.pathname;
  return routeToView[path] || 'dashboard';
}

// Get initial auth view from URL
function getInitialAuthView(): AuthView {
  const path = window.location.pathname;
  return authRoutes[path] || 'login';
}

// Public routes that don't require authentication
const publicRoutes = ['/landing-test'];

// Check if current path is a protected route
function isProtectedRoute(): boolean {
  const path = window.location.pathname;
  // Auth routes are not protected
  if (authRoutes[path]) return false;
  // Public routes are not protected
  if (publicRoutes.includes(path)) return false;
  // API routes are not handled by frontend
  if (path.startsWith('/api/')) return false;
  // Everything else is protected
  return true;
}

function App() {
  // React Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Auth state
  const { user, isLoading: authLoading, isAuthenticated, isEmailVerified, hasDropCatchCredentials, userConfig, updateConfig, isAdmin, logout: _logout } = useAuth();
  const [authView, setAuthView] = useState<AuthView>(getInitialAuthView);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState<string>("");

  // App state
  const [currentUsername, setCurrentUsername] = useState<string>("");
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);
  const [currentView, setCurrentView] = useState<NavigationView>(getInitialView);
  const [showSettings, setShowSettings] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [categories, setCategories] = useState<DomainCategory[]>(DEFAULT_CATEGORIES);
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const [_activeCategory, setActiveCategory] = useState<string>("law");

  // Navigation function that updates both state and URL
  const navigateTo = useCallback((view: NavigationView) => {
    setCurrentView(view);
    const route = viewToRoute[view];
    navigate(route);
  }, [navigate]);

  // Sync currentView with React Router location
  useEffect(() => {
    const viewFromPath = routeToView[location.pathname];
    if (viewFromPath && viewFromPath !== currentView) {
      setCurrentView(viewFromPath);
    }
  }, [location.pathname, currentView]);

  // Initialize services that depend on user
  const batchAnalysisService = useMemo(() => getBatchAnalysisService(geminiService), []);
  const portfolioService = useMemo(
    () => (currentUsername ? createPortfolioService(geminiService, currentUsername) : null),
    [currentUsername]
  );

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Listen for openSettings event from Dashboard
  useEffect(() => {
    const handleOpenSettings = () => setShowSettings(true);
    window.addEventListener('openSettings', handleOpenSettings);
    return () => window.removeEventListener('openSettings', handleOpenSettings);
  }, []);

  // Load config from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("zero_point_domains_config");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConfig(parsed);
        dropcatchService.updateConfig(parsed);
        geminiService.updateApiKey(parsed.google_api_key);
        resendService.updateApiKey(parsed.resend_api_key);
      } catch (e) {
        console.error("Failed to load config:", e);
      }
    }
  }, []);

  const handleSaveConfig = (newConfig: AppConfig) => {
    setConfig(newConfig);
    localStorage.setItem("zero_point_domains_config", JSON.stringify(newConfig));
    dropcatchService.updateConfig(newConfig);
    geminiService.updateApiKey(newConfig.google_api_key);
    resendService.updateApiKey(newConfig.resend_api_key);
  };

  const navItems: { id: NavigationView; label: string; shortcut: string }[] = [
    { id: "dashboard", label: "DASHBOARD", shortcut: "F1" },
    { id: "auctions", label: "SEARCH", shortcut: "F2" },
    { id: "backorders", label: "BACKORDERS", shortcut: "F3" },
    { id: "bids", label: "MY BIDS", shortcut: "F4" },
    { id: "watchlist", label: "WATCHLIST", shortcut: "F5" },
    { id: "portfolio", label: "PORTFOLIO", shortcut: "F6" },
    { id: "analytics", label: "ANALYTICS", shortcut: "F7" },
    { id: "history", label: "HISTORY", shortcut: "F8" },
    { id: "chat", label: "AI CHAT", shortcut: "F9" },
    { id: "automations", label: "AUTOMATIONS", shortcut: "F10" },
    { id: "whois", label: "WHOIS", shortcut: "F11" },
    { id: "status", label: "STATUS", shortcut: "F12" },
  ];

  const renderContent = () => {
    switch (currentView) {
      case "dashboard":
        return (
          <Dashboard
            config={config}
            dropcatchService={dropcatchService}
            geminiService={geminiService}
            userName={currentUsername}
            isEmailVerified={isEmailVerified}
            userCreatedAt={user?.createdAt}
            hasDropCatchCredentials={hasDropCatchCredentials}
            savedKeywords={searchKeywords}
            onResendVerification={handleResendVerification}
            onSaveKeywords={(keywords) => {
              setSearchKeywords(keywords);
              if (currentUsername) {
                const prefs = storageService.getUserPreferences(currentUsername);
                storageService.saveUserPreferences(currentUsername, {
                  ...prefs,
                  searchKeywords: keywords,
                });
              }
            }}
          />
        );
      case "auctions":
        return (
          <AuctionSearch
            config={config}
            dropcatchService={dropcatchService}
            geminiService={geminiService}
          />
        );
      case "backorders":
        return (
          <BackorderManager
            config={config}
            dropcatchService={dropcatchService}
            geminiService={geminiService}
          />
        );
      case "bids":
        return <BidsManager config={config} dropcatchService={dropcatchService} />;
      case "history":
        return <HistoryView config={config} dropcatchService={dropcatchService} />;
      case "watchlist":
        return (
          <Watchlist
            items={watchlistItems}
            categories={categories}
            dropcatchService={dropcatchService}
            batchAnalysisService={batchAnalysisService}
            onItemsChange={(items) => {
              setWatchlistItems(items);
              if (currentUsername) {
                const prefs = storageService.getUserPreferences(currentUsername);
                storageService.saveUserPreferences(currentUsername, {
                  ...prefs,
                  watchlistKeywords: items,
                });
              }
            }}
            onBackorder={(domain, maxBid) => {
              dropcatchService.createBackorders([{ domain, amount: maxBid }]);
            }}
            className="h-[calc(100vh-120px)]"
          />
        );
      case "portfolio":
        return portfolioService ? (
          <Portfolio
            portfolioService={portfolioService}
            categories={categories}
            className="h-[calc(100vh-120px)]"
          />
        ) : (
          <div className="p-6">
            <div className="text-green-600">Please log in to view portfolio.</div>
          </div>
        );
      case "analytics":
        return (
          <Analytics
            dropcatchService={dropcatchService}
            categories={categories}
            className="h-[calc(100vh-120px)]"
          />
        );
      case "chat":
        return (
          <ChatPage
            geminiService={geminiService}
            dropcatchService={dropcatchService}
            onNavigate={(view) => navigateTo(view as NavigationView)}
          />
        );
      case "automations":
        return (
          <Automations
            username={currentUsername}
            className="h-[calc(100vh-120px)]"
          />
        );
      case "whois":
        return (
          <WhoisLookup
            geminiService={geminiService}
            onNavigateToChat={(_domain) => {
              // Navigate to chat (domain context can be added later)
              navigateTo("chat");
            }}
          />
        );
      case "status":
        return (
          <StatusPage
            dropcatchService={dropcatchService}
            geminiService={geminiService}
            resendService={resendService}
            className="h-[calc(100vh-120px)]"
          />
        );
      default:
        return null;
    }
  };

  const isConfigured = config.dropcatch_client_id && config.dropcatch_client_secret;
  const hasAiKey = config.google_api_key;

  // Sync user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const username = user.name || user.email;
      storageService.setCurrentUser(username);
      setCurrentUsername(username);

      // Load user-specific config from userConfig (from database) or fallback to localStorage
      if (userConfig) {
        const newConfig: AppConfig = {
          ...DEFAULT_CONFIG,
          dropcatch_client_id: userConfig.dropcatch_client_id || "",
          dropcatch_client_secret: userConfig.dropcatch_client_secret || "",
          google_api_key: userConfig.google_api_key || "",
          resend_api_key: userConfig.resend_api_key || "",
          email_notifications: userConfig.email_notifications || false,
          notification_email: userConfig.notification_email || "",
        };
        setConfig(newConfig);
        dropcatchService.updateConfig(newConfig);
        geminiService.updateApiKey(newConfig.google_api_key);
        resendService.updateApiKey(newConfig.resend_api_key);
      }

      // Load user preferences from localStorage (will migrate to DB later)
      const prefs = storageService.getUserPreferences(username);
      setCategories(prefs.categories || DEFAULT_CATEGORIES);
      setWatchlistItems(prefs.watchlistKeywords || []);
      setSearchKeywords((prefs as any).searchKeywords || []);
      setActiveCategory(storageService.getUserProfile(username)?.activeCategory || "law");

      // Initialize alert service
      alertService.initialize(dropcatchService, resendService);
      alertService.startMonitoring(username);
    }
  }, [isAuthenticated, user, userConfig]);

  // Handle auth navigation with URL updates
  const handleAuthNavigate = useCallback((page: AuthView | "dashboard") => {
    if (page === "dashboard") {
      // Redirect to dashboard using React Router
      navigate('/dashboard', { replace: true });
      return;
    }
    setAuthView(page);
    // Update URL for auth pages
    const authRouteMap: Record<AuthView, string> = {
      'login': '/login',
      'signup': '/signup',
      'forgot-password': '/forgot-password',
      'reset-password': '/reset-password',
      'verify-email': '/verify-email',
    };
    navigate(authRouteMap[page]);
  }, [navigate]);

  // Redirect unauthenticated users trying to access protected routes
  useEffect(() => {
    if (!authLoading && !isAuthenticated && isProtectedRoute()) {
      // Store the intended destination for redirect after login
      const intendedPath = location.pathname;
      if (intendedPath !== '/' && intendedPath !== '/dashboard') {
        sessionStorage.setItem('redirectAfterLogin', intendedPath);
      }
      // Redirect to login
      navigate('/login', { replace: true });
      setAuthView('login');
    }
  }, [authLoading, isAuthenticated, location.pathname, navigate]);

  // Redirect authenticated users away from auth pages to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated && authRoutes[location.pathname]) {
      // Check if there's a stored redirect path
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, location.pathname, navigate]);

  // Handle resend verification email
  const handleResendVerification = useCallback(async () => {
    if (!user?.email) return;
    try {
      await sendVerificationEmail({ 
        email: user.email, 
        callbackURL: `${window.location.origin}/verify-email` 
      });
    } catch (error) {
      console.error("Failed to resend verification email:", error);
    }
  }, [user?.email]);

  // Show loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-green-500 text-xl animate-pulse">LOADING...</div>
      </div>
    );
  }

  // Show landing page for /landing-test route (accessible to everyone)
  if (location.pathname === '/landing-test') {
    return <LandingPage />;
  }


  // Show auth pages if not authenticated
  if (!isAuthenticated) {
    // Check URL for auth-related tokens
    const urlParams = new URLSearchParams(location.search);
    const path = location.pathname;

    // Handle reset-password with token
    if (urlParams.get("token") && (path === '/reset-password' || path.includes("reset-password"))) {
      return <ResetPasswordPage onNavigate={handleAuthNavigate} />;
    }

    // Handle verify-email with token
    if (urlParams.get("token") && (path === '/verify-email' || path.includes("verify-email"))) {
      return <VerifyEmailPage onNavigate={handleAuthNavigate} />;
    }

    // Route based on URL path or state
    const currentAuthView = authRoutes[path] || authView;

    switch (currentAuthView) {
      case "signup":
        return (
          <SignupPage
            onNavigate={handleAuthNavigate}
            onSuccess={(email) => {
              setPendingVerifyEmail(email);
              handleAuthNavigate("verify-email");
            }}
          />
        );
      case "forgot-password":
        return <ForgotPasswordPage onNavigate={handleAuthNavigate} />;
      case "reset-password":
        return <ResetPasswordPage onNavigate={handleAuthNavigate} />;
      case "verify-email":
        return (
          <VerifyEmailPage
            email={pendingVerifyEmail}
            onNavigate={handleAuthNavigate}
          />
        );
      default:
        return (
          <LoginPage
            onNavigate={handleAuthNavigate}
          />
        );
    }
  }

  return (
    <div
      className="min-h-screen bg-black font-mono text-green-500 flex"
      style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
    >
      {/* CRT Scanline Effect */}
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.1)_50%)] bg-[length:100%_4px] z-50" />

      {/* CRT Flicker Effect */}
      <div className="pointer-events-none fixed inset-0 bg-green-500/[0.02] animate-pulse z-40" />

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden md:flex flex-col bg-black border-r-2 border-green-500 fixed inset-y-0 left-0 z-30 transition-all duration-300 ${
          isSidebarOpen ? "w-64" : "w-20"
        }`}
        style={{ boxShadow: "2px 0 20px rgba(34, 197, 94, 0.3)" }}
      >
        {/* Logo */}
        <div className="p-4 border-b-2 border-green-500">
          {isSidebarOpen ? (
            <div className="flex items-center gap-3">
              <LogoCircuit size="sm" animate={true} showText={false} />
              <div>
                <pre className="text-green-400 text-[6px] leading-tight font-bold whitespace-pre" style={{ textShadow: "0 0 10px rgba(52, 211, 153, 0.8)" }}>
{`▀▀█ █▀▀ █▀█ █▀█   █▀█ █▀█ █ █▄ █ ▀█▀
█▄▄ ██▄ █▀▄ █▄█   █▀▀ █▄█ █ █ ▀█  █
        █▀▄ █▀█ █▀▄▀█ ▄▀█ █ █▄ █ █▀
        █▄▀ █▄█ █ ▀ █ █▀█ █ █ ▀█ ▄█`}
                </pre>
              </div>
            </div>
          ) : (
            <LogoCircuit size="sm" animate={true} showText={false} />
          )}
        </div>

        {/* System Status */}
        {isSidebarOpen && (
          <div className="px-4 py-2 border-b border-green-900 text-xs text-green-600">
            <div className="flex justify-between">
              <span>SYS TIME:</span>
              <span className="text-green-400 tabular-nums">
                {currentTime.toLocaleTimeString("en-US", { hour12: false })}
              </span>
            </div>
            <div className="flex justify-between">
              <span>MEM:</span>
              <span className="text-green-400">{Math.floor(Math.random() * 640)}K FREE</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => navigateTo(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 font-medium transition-all tracking-wider text-sm ${
                currentView === item.id
                  ? "bg-green-500 text-black"
                  : "text-green-500 hover:bg-green-900/50 border border-transparent hover:border-green-700"
              }`}
            >
              <span className="text-green-600 text-xs">[{item.shortcut}]</span>
              {isSidebarOpen && <span>{item.label}</span>}
              {!isSidebarOpen && <span>{index + 1}</span>}
            </button>
          ))}
        </nav>

        {/* Settings Button */}
        <div className="p-2 border-t border-green-900">
          <button
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center gap-3 px-3 py-2 text-green-500 hover:bg-green-900/50 border border-transparent hover:border-green-700 font-medium tracking-wider text-sm transition-all"
          >
            <span className="text-green-600 text-xs">[F10]</span>
            {isSidebarOpen && <span>CONFIG</span>}
          </button>
        </div>

        {/* Status Indicator */}
        {isSidebarOpen && (
          <div className="p-3 border-t-2 border-green-500">
            <div className="p-3 border border-green-700 bg-black">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`w-2 h-2 ${
                    isConfigured && hasAiKey ? "bg-green-500 animate-pulse" : "bg-green-800"
                  }`}
                />
                <span className="text-xs font-semibold tracking-wider">
                  {isConfigured && hasAiKey ? "ONLINE" : "OFFLINE"}
                </span>
              </div>
              <p className="text-xs text-green-700">
                {isConfigured && hasAiKey
                  ? "ALL SYSTEMS NOMINAL"
                  : "RUN CONFIG.EXE"}
              </p>
            </div>
          </div>
        )}

        {/* Collapse Toggle */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="absolute -right-4 top-20 w-8 h-8 bg-black border-2 border-green-500 flex items-center justify-center hover:bg-green-900 transition-colors"
          style={{ boxShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
        >
          <span className="text-green-500 text-xs">
            {isSidebarOpen ? "<<" : ">>"}
          </span>
        </button>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-20 bg-black border-b-2 border-green-500 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-green-500 font-bold">{"<ZERO POINT DOMAINS>"}</span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-green-500 border border-green-500 hover:bg-green-900"
          >
            {isMobileMenuOpen ? "[X]" : "[=]"}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/90" onClick={() => setIsMobileMenuOpen(false)}>
          <div
            className="absolute top-0 right-0 w-64 h-full bg-black border-l-2 border-green-500"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "-2px 0 20px rgba(34, 197, 94, 0.3)" }}
          >
            <div className="p-4 border-b-2 border-green-500">
              <h2 className="font-bold text-green-400 tracking-wider">MAIN MENU</h2>
            </div>
            <nav className="p-2 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    navigateTo(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 font-medium tracking-wider text-sm transition-colors ${
                    currentView === item.id
                      ? "bg-green-500 text-black"
                      : "text-green-500 hover:bg-green-900/50"
                  }`}
                >
                  <span className="text-green-600 text-xs">[{item.shortcut}]</span>
                  <span>{item.label}</span>
                </button>
              ))}
              <button
                onClick={() => {
                  setShowSettings(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-green-500 hover:bg-green-900/50 font-medium tracking-wider text-sm"
              >
                <span className="text-green-600 text-xs">[F10]</span>
                <span>CONFIG</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 overflow-auto min-h-screen transition-all duration-300 ${
          isSidebarOpen ? "md:ml-64" : "md:ml-20"
        } pt-16 md:pt-0`}
      >
        {/* Top Bar */}
        <div className="hidden md:flex items-center justify-between px-6 py-2 border-b-2 border-green-500 bg-black">
          <div className="flex items-center gap-4 text-xs text-green-600">
            <span>TERMINAL: /usr/zeropoint/{currentView}</span>
            <span className="animate-pulse">|</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-green-600">DATE:</span>
            <span className="text-green-400 tabular-nums">
              {currentTime.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })}
            </span>
            <span className="text-green-600">TIME:</span>
            <span className="text-green-400 tabular-nums">
              {currentTime.toLocaleTimeString("en-US", { hour12: false })}
            </span>
            <span className="text-green-500 animate-pulse">_</span>
          </div>
        </div>

        {renderContent()}

        {/* Footer */}
        <div className="px-6 py-2 border-t-2 border-green-500 bg-black text-xs flex justify-between text-green-600">
          <span>(C) 2025 THE ZERO ENERGY GROUP</span>
          <span className="animate-pulse">*** READY ***</span>
        </div>
      </main>

      {/* Settings Modal */}
      {showSettings && (
        <Settings
          config={config}
          onSave={handleSaveConfig}
          onClose={() => setShowSettings(false)}
          dropcatchService={dropcatchService}
          isAdmin={isAdmin}
          userConfig={userConfig}
          onUpdateUserConfig={updateConfig}
        />
      )}
    </div>
  );
}

export default App;
